import React, { useContext, useEffect, useState, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Content } from './style'
import { ButtonCreate, LeftSide, OptionsButton, CompanyTypes, RightSide, SectionName, SubHeaderContainer } from '../../../components/styles/subheader-style'
import TableCompanies from './CompaniesTable'
import ModalContext from '../../../contexts/ModalContext'
import FieldsManager, { FieldsManagerType } from '../../../components/FieldManager'
import { Settings2 } from 'lucide-react';
import { tableFields } from '../../../components/FieldManager/table-fields'
import WithoutSelectedFields from '../../SystemErrors/WithoutSelectedFields'
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'
import FilterMenu from '../../../components/SubHeaderFilter/FilterMenu'
import { Header } from '../../../components/SubHeaderFilter/types'
import AppliedFiltersDisplay from '../../../components/SubHeaderFilter/AppliedFiltersDisplay'
import { Filter } from '../../../components/SubHeaderFilter/types'
import { RefreshIconContainer, FilterWrapper } from '../../../components/styles/subheader-style'
import { RefreshCw, Filter as FilterIcon } from 'lucide-react'
import { TypeMainOutlet } from '..'

const CompaniesSubHeader: React.FC<{
  appliedFilters: Filter[];
  onApplyFilter: (f: Filter) => void;
  fields: FieldsManagerType;
  personType: 'Jurídica' | 'Física' | null;
  setPersonType: React.Dispatch<React.SetStateAction<'Jurídica' | 'Física' | null>>;
}> = ({ appliedFilters, onApplyFilter, fields, personType, setPersonType }) => {
  const { setModalActive, setModalSection } = useContext(ModalContext)
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const filterButtonRef = useRef<HTMLDivElement | null>(null)
  const queryClient = useQueryClient()

  const navigate = useNavigate()
  const createCompany = () => {
    navigate('create')
  }

  const handleSetPersonType = (type: 'Jurídica' | 'Física') => {
    if (type === personType) {
      setPersonType(null)
      return
    }
    setPersonType(type)
  }

  const visibleFieldsForFilter = fields.whichFieldsToShow.filter((f: any) => f.checked)

  const headerOptions: Header[] = visibleFieldsForFilter.map((f: any) => {
    if (f.field === 'companyTypes') {
      return { key: f.field, label: f.fieldName, type: 'string', selectOptions: [
        { value: 'Cliente', label: 'Cliente' },
        { value: 'Fornecedor', label: 'Fornecedor' },
        { value: 'Ambos', label: 'Ambos' }
      ] } as Header
    }

    if (f.field === 'contactName') {
      const dynamicLabel = 'Nome'
      return { key: f.field, label: dynamicLabel, type: 'string' } as Header
    }

    return { key: f.field, label: f.fieldName, type: 'string' } as Header
  })


  return (
    <SubHeaderContainer>
      <LeftSide>
        <SectionName>Empresas</SectionName>
      </LeftSide>
      <RightSide>
        <FilterWrapper style={{ marginRight: 8, display: 'flex', alignItems: 'center' }}>
          <CompanyTypes onClick={() => handleSetPersonType('Jurídica')} className={personType === 'Jurídica' ? 'actived' : ''} title="Juridica">JURÍDICA</CompanyTypes>
          <CompanyTypes onClick={() => handleSetPersonType('Física')} className={personType === 'Física' ? 'actived' : ''} title="Fisica" style={{ marginLeft: 8 }}>FÍSICA</CompanyTypes>
        </FilterWrapper>
        <RefreshIconContainer onClick={() => { queryClient.invalidateQueries({ queryKey: ['companies'] }); }} title="Atualizar">
          <RefreshCw size={20} />
        </RefreshIconContainer>
        <FilterWrapper>
          <OptionsButton ref={filterButtonRef} onClick={() => setIsFilterMenuOpen(true)}>
            <FilterIcon size={10} />
          </OptionsButton>
          {isFilterMenuOpen && (
            <FilterMenu
              headers={headerOptions}
              onApplyFilter={(f) => onApplyFilter(f)}
              onClose={() => setIsFilterMenuOpen(false)}
              anchorRef={filterButtonRef}
              filters={appliedFilters}
            />
          )}
        </FilterWrapper>
        <OptionsButton style={{ visibility: 'hidden' }}><Settings2 /></OptionsButton>
        <ButtonCreate onClick={() => createCompany()}>+ Cadastrar empresa</ButtonCreate>
      </RightSide>
    </SubHeaderContainer>
  )
}

const CompaniesContent = () => {
  const [isdropdownOpen, setIsDropdownOpen] = useState(false)
  const { search } = useOutletContext<TypeMainOutlet>()
  const [refresh, setRefresh] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Filter[]>([]);
  const [personType, setPersonType] = useState<'Jurídica' | 'Física' | null>(null)
  const [fields, setFields] = useState(() => {
    const fromLocalStorage = localStorage.getItem('tableFields')
    if (fromLocalStorage) {
      let tableFieldsFromLocalStorage: FieldsManagerType[] | null = null
      try {
        tableFieldsFromLocalStorage = JSON.parse(fromLocalStorage)
      } catch (e) {
        tableFieldsFromLocalStorage = null
      }
      
    }

    const defaultCompanies = tableFields.filter(field => field.tableSection === 'companies')[0]
    if (fromLocalStorage) {
      try {
        const parsed = JSON.parse(fromLocalStorage)
        const storedCompanies = parsed && Array.isArray(parsed) ? parsed.find((t:any)=>t.tableSection==='companies') : null
        if (storedCompanies) {
          const merged = { ...defaultCompanies, whichFieldsToShow: defaultCompanies.whichFieldsToShow.map(df => {
            const found = storedCompanies.whichFieldsToShow.find((sf:any) => sf.field === df.field)
            return found ? { ...df, checked: found.checked } : df
          }) }
          return merged
        }
      } catch (e) {
        console.error('Failed parsing tableFields from localStorage', e)
      }
    }

    return defaultCompanies
  })

  useEffect(() => {
    const fromLocalStorage = localStorage.getItem('tableFields')
    const tableFieldsJson = fromLocalStorage ? JSON.parse(fromLocalStorage) : tableFields
    const toLocalStorage = tableFieldsJson.map((table: FieldsManagerType) => {
      if (table.tableSection === 'companies') {
        return { ...table, whichFieldsToShow: fields.whichFieldsToShow }
      }
      return table
    })

    localStorage.setItem('tableFields', JSON.stringify(toLocalStorage))
  }, [fields])

  const checkedFieldsCount = fields.whichFieldsToShow.filter((field: any) => field.checked).length

  const handleAddFilter = (f: Filter) => {
    let cleanedValue = typeof f.value === 'string' ? f.value.replace(/\s+/g, ' ').trim() : f.value;
    let cleanedDisplay = typeof f.displayValue === 'string' ? String(f.displayValue).replace(/\s+/g, ' ').trim() : f.displayValue;

    if (f.key === 'address' && typeof cleanedValue === 'string') {
      const digits = cleanedValue.replace(/\D/g, '');
      if (digits.length > 0) {
        const padded = digits.padStart(8, '0');
        cleanedValue = digits;
        cleanedDisplay = `${padded.slice(0, 5)}-${padded.slice(5)}`;
      }
    }

    const cleanedFilter = { ...f, value: cleanedValue, displayValue: cleanedDisplay };
    setAppliedFilters(prev => [...prev, cleanedFilter]);
  }

  useEffect(() => {
    // Ao mudar o filtro rápido (Jurídica/Física) ajusta automaticamente as colunas relevantes
    const juridicaFields = ['cnpj', 'companyName', 'stateRegistration'];
    const fisicaFields = ['cpf', 'contactName'];

    if (!personType) return

    setFields(prev => ({
      ...prev,
      whichFieldsToShow: prev.whichFieldsToShow.map(f => {
        if (personType === 'Jurídica' && juridicaFields.includes(f.field)) {
          return { ...f, checked: true }
        }
        if (personType === 'Física' && fisicaFields.includes(f.field)) {
          return { ...f, checked: true }
        }
        if (personType === 'Jurídica' && fisicaFields.includes(f.field)) {
          return { ...f, checked: false }
        }
        if (personType === 'Física' && juridicaFields.includes(f.field)) {
          return { ...f, checked: false }
        }
        return f
      })
    }))
  }, [personType])

  return (
    <>
      <CompaniesSubHeader appliedFilters={appliedFilters} onApplyFilter={handleAddFilter} fields={fields} personType={personType} setPersonType={setPersonType} />
      <AppliedFiltersDisplay
        filters={appliedFilters}
        onRemoveFilter={(i: number) => setAppliedFilters(prev => prev.filter((_, idx) => idx !== i))}
        onClearAll={() => {
          setAppliedFilters([])
          setPersonType(null)
        }}
      />
      <Content>
        {checkedFieldsCount === 0 ?
          <WithoutSelectedFields dropdown={setIsDropdownOpen} />
          :
          <>
            <TableCompanies dropdownOpen={setIsDropdownOpen} whichFieldsToShow={fields} appliedFilters={appliedFilters} refresh={refresh} personType={personType} search={search} />
          </>
        }
        {isdropdownOpen ? <FieldsManager dropdownOpen={setIsDropdownOpen} fields={fields} setFields={setFields} /> : null}
        <Outlet
          context={search}
        />
      </Content>
    </>
  )
}
export default CompaniesContent
